import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Divider, Paper, FormGroup, FormControlLabel, Checkbox, 
  Button, CircularProgress, IconButton, Tooltip, Dialog, DialogTitle, 
  DialogContent, DialogContentText, DialogActions, Chip, Grid 
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../api';

const NOTIFICATION_TYPES = [
  { key: 'employment_letter', label: 'Employment Letter (Personal Email + PDF Attachment)' },
  { key: 'document_upload_link', label: 'Upload Document Link (Portal & QR Code)' },
  { key: 'welcome_email', label: 'Welcome Email (Official Email)' },
  { key: 'manager_notification', label: 'Notification to L1 Manager' },
  { key: 'it_notification', label: 'Notification to IT Support' },
  { key: 'department_notification', label: 'Notification to Department' },
  { key: 'company_notification', label: 'Company-wide Welcome' }
];

const DOC_OPTIONS = [
  "Aadhar", "PAN", "ELA (Transact Global - Employment Letter)", 
  "10th Certificate", "12th Certificate", "Experience letters", 
  "Degree certificates", "Passport", "Pay Slips", "Bank Statement"
];

const NotificationSystem = ({ employeeId, showMsg }) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState({});
  const [config, setConfig] = useState({
    employment_letter: false,
    document_upload_link: false,
    welcome_email: false,
    manager_notification: false,
    it_notification: false,
    department_notification: false,
    company_notification: false
  });

  // Dialog States
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resendList, setResendList] = useState([]);
  
  // Document Selection Modal State
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);

  const baseUrl = api.defaults.baseURL || `http://${window.location.hostname}:8000/api/v1`;

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line
  }, [employeeId]);

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/employees/${employeeId}/notifications/history`);
      setHistory(res.data || {});
    } catch (err) {
      console.error("Failed to fetch notification history", err);
    }
  };

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setConfig(prev => ({ ...prev, [name]: checked }));
  };

  const handleDocToggle = (doc) => {
    setSelectedDocs(prev => 
      prev.includes(doc) ? prev.filter(d => d !== doc) : [...prev, doc]
    );
  };

  // Step 1: Pre-send duplicate check
  const handlePreSendCheck = () => {
    const selected = Object.keys(config).filter(key => config[key]);
    if (selected.length === 0) return showMsg("Please select at least one notification to send.", "warning");

    const alreadySent = selected.filter(key => history[key]);
    
    if (alreadySent.length > 0) {
      const displayNames = alreadySent.map(k => NOTIFICATION_TYPES.find(n => n.key === k).label);
      setResendList(displayNames);
      setDialogOpen(true);
    } else {
      checkDocLinkRequirement();
    }
  };

  // Step 2: Proceed from duplicate warning
  const handleResendConfirm = () => {
    setDialogOpen(false);
    checkDocLinkRequirement();
  };

  // Step 3: Check if we need to open the Document Selection Modal
  const checkDocLinkRequirement = () => {
    if (config.document_upload_link) {
      setDocModalOpen(true);
    } else {
      executeSend();
    }
  };

  // Step 4: Final Execution
  const executeSend = async () => {
    if (config.document_upload_link && selectedDocs.length === 0) {
      return showMsg("Please select at least one document for the upload link.", "warning");
    }

    setDocModalOpen(false);
    setLoading(true);
    
    try {
      // Split standard notifications from the document request
      const standardConfig = { ...config };
      delete standardConfig.document_upload_link;
      
      const hasStandard = Object.values(standardConfig).some(val => val === true);

      // Fire Standard Notifications
      if (hasStandard) {
        await api.post(`/employees/${employeeId}/notifications`, standardConfig);
      }

      // Fire Document Link Generation separately
      if (config.document_upload_link) {
        await api.post(`/document-requests/generate/${employeeId}`, selectedDocs);
        
        // Log it manually to standard history for UI consistency
        // Silent catch if log endpoint doesn't exist yet
      }

      showMsg("Selected notifications dispatched successfully!");
      
      setConfig({
        employment_letter: false, document_upload_link: false, welcome_email: false, 
        manager_notification: false, it_notification: false, 
        department_notification: false, company_notification: false
      });
      setSelectedDocs([]);
      fetchHistory();
      
    } catch (err) {
      showMsg("Failed to dispatch notifications. Check server logs.", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = (key) => {
    const record = history[key];
    if (!record) return <Chip label="Not Sent" size="small" sx={{ ml: 2, height: 20, fontSize: '0.7rem' }} />;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 2, gap: 0.5 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 18 }} />
        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold' }}>
          Sent on {record.sent_on} by {record.sent_by}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Notification System
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Dispatch automated emails and formal documents. View history to prevent duplicates.
        </Typography>
      </Box>
      <Divider sx={{ mb: 4 }} />

      <Paper elevation={0} sx={{ p: 4, mb: 4, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
        <FormGroup sx={{ gap: 2 }}>
          
          {/* Main Employment Letter Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel 
                sx={{ minWidth: 350 }}
                control={<Checkbox checked={config.employment_letter} onChange={handleChange} name="employment_letter" />} 
                label={<Typography fontWeight="500">{NOTIFICATION_TYPES[0].label}</Typography>} 
              />
              {renderStatus('employment_letter')}
            </Box>
            
            {history['employment_letter']?.has_file && (
              <Tooltip title="Download Generated Letter">
                <IconButton color="primary" onClick={() => window.open(`${baseUrl}/employees/${employeeId}/notifications/download-letter`, '_blank')}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          
          <Divider sx={{ my: 1 }} />

          {/* Vertical Flex Box for Line-by-Line Rendering */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 1, pt: 1 }}>
            {NOTIFICATION_TYPES.slice(1).map((type) => (
              <Box key={type.key} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel 
                  sx={{ minWidth: 350 }}
                  control={<Checkbox checked={config[type.key]} onChange={handleChange} name={type.key} />} 
                  label={<Typography fontWeight="500">{type.label}</Typography>} 
                />
                {renderStatus(type.key)}
              </Box>
            ))}
          </Box>
        </FormGroup>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          variant="contained" 
          color="primary" 
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />} 
          onClick={handlePreSendCheck}
          disabled={loading}
        >
          {loading ? 'Dispatching...' : 'Send Selected Notifications'}
        </Button>
      </Box>

      {/* Duplicate Dispatch Warning Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'warning.main' }}>Duplicate Dispatch Warning</DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            The following notifications have already been sent previously. Are you sure you want to resend them?
          </DialogContentText>
          <Box sx={{ mt: 1, pl: 2, borderLeft: '3px solid #ffa726' }}>
            {resendList.map((item, idx) => (
              <Typography key={idx} variant="body2" sx={{ mb: 0.5, color: '#444', fontWeight: 'bold' }}>
                • {item}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleResendConfirm} variant="contained" color="warning">Yes, Resend Notifications</Button>
        </DialogActions>
      </Dialog>

      {/* Document Selection Modal */}
      <Dialog open={docModalOpen} onClose={() => setDocModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main', bgcolor: '#f0f4ff' }}>
          Select Documents to Request
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText sx={{ mb: 3 }}>
            Please select the specific documents you require the employee to upload via the secure portal.
          </DialogContentText>
          <Grid container spacing={2}>
            {DOC_OPTIONS.map(doc => (
              <Grid item xs={12} sm={6} md={4} key={doc}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={selectedDocs.includes(doc)} 
                      onChange={() => handleDocToggle(doc)} 
                      color="primary"
                    />
                  }
                  label={<Typography variant="body2">{doc}</Typography>}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#fafafa' }}>
          <Button onClick={() => setDocModalOpen(false)} color="inherit">Cancel</Button>
          <Button 
            onClick={executeSend} 
            variant="contained" 
            color="primary" 
            disabled={selectedDocs.length === 0}
            startIcon={<SendIcon />}
          >
            Generate Link & Dispatch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationSystem;